import { useEffect, useRef, useState } from 'react';
import { useTheme } from '../../../../theme-context';
import { useAppDispatch } from '../../../../app/hooks';
import {
    EXEC_CHAT_ACTIONS,
    ExecChatAction,
} from '../../../../constants/chatActions';
import { openChat } from '../../../../store/brokers/partners/chats/ChatsUISlice';

type Props = {
    /** When true, command bar matches waiting/disabled spec (opacity, not-allowed). */
    disabled?: boolean;
};

export function ExecViewChatEntry({ disabled = false }: Props) {
    const dispatch = useAppDispatch();
    const { theme } = useTheme();
    const [input, setInput] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isListening, setIsListening] = useState(false);
    const [showActions, setShowActions] = useState(false);
    const recognitionRef = useRef<{
        start: () => void;
        stop: () => void;
    } | null>(null);

    useEffect(() => {
        /* eslint-disable @typescript-eslint/no-explicit-any */
        const SpeechRecognition =
            (window as any).SpeechRecognition ||
            (window as any).webkitSpeechRecognition;
        if (!SpeechRecognition) return;
        const recognition = new SpeechRecognition();
        recognition.continuous = false;
        recognition.interimResults = false;
        recognition.lang = 'en-US';
        recognition.onresult = (event: {
            results: { 0: { 0: { transcript: string } } };
        }) => {
            const transcript = event.results[0][0].transcript;
            setInput((prev) => `${prev} ${transcript}`.trim());
        };
        recognition.onerror = () => setIsListening(false);
        recognition.onend = () => setIsListening(false);
        recognitionRef.current = recognition;
    }, []);

    const handleMicClick = () => {
        if (disabled) return;
        const r = recognitionRef.current;
        if (!r) return;
        if (isListening) {
            r.stop();
            setIsListening(false);
        } else {
            r.start();
            setIsListening(true);
        }
    };

    const onClickAction = (action: ExecChatAction) => {
        if (disabled) return;
        dispatch(
            openChat({
                action,
                data: [
                    {
                        user: {
                            message: action.text,
                            files: [],
                            sort: 2,
                            createdAt: new Date(),
                        },
                    },
                ],
            }),
        );
    };

    const onSubmitInput = () => {
        if (disabled || !input.trim()) return;
        dispatch(
            openChat({
                data: [
                    {
                        user: {
                            message: input.trim(),
                            files: [],
                            sort: 2,
                            createdAt: new Date(),
                        },
                    },
                ],
            }),
        );
        setInput('');
    };

    const toggleActions = () => {
        if (disabled) return;
        setShowActions((prev) => !prev);
    };

    const isDark = theme === 'dark';

    const barBorder = 'border-2 border-[#00330F]';
    const barBg = isDark ? 'bg-[#111317]' : 'bg-white';

    return (
        <div className="-ml-5 w-[calc(100%+2.5rem)] max-w-none min-w-0 shrink-0 self-stretch">
            <section
                className="flex w-full min-w-0 max-w-none flex-col self-stretch px-5 py-2"
                style={{ fontFamily: 'Inter, sans-serif' }}
                aria-label="Manage users command bar">
                {/* 60px executive command bar — Rebecca UI spec */}
                <div
                    className={[
                        'flex h-[60px] w-full min-w-0 shrink-0 items-center px-[14px]',
                        barBorder,
                        'rounded-[16px]',
                        barBg,
                        disabled ? 'cursor-not-allowed opacity-60' : '',
                    ].join(' ')}>
                    <button
                        type="button"
                        onClick={handleMicClick}
                        disabled={disabled}
                        aria-label={
                            isListening ? 'Stop listening' : 'Start listening'
                        }
                        className={[
                            'relative mr-3 flex h-7 w-7 shrink-0 items-center justify-center',
                            disabled
                                ? 'cursor-not-allowed opacity-50'
                                : 'cursor-pointer',
                        ].join(' ')}>
                        <div
                            className={`relative h-7 w-7 ${
                                isListening ? 'animate-pulse' : ''
                            }`}>
                            <img
                                src={
                                    isDark
                                        ? '/assets/chat/microphone-white.svg'
                                        : '/assets/chat/microphone.svg'
                                }
                                className="h-full w-full object-contain"
                                alt=""
                            />
                            {isListening && !disabled && (
                                <span className="pointer-events-none absolute inset-0 -z-10 rounded-full border-2 border-blue-400 animate-ping" />
                            )}
                        </div>
                    </button>

                    <input
                        type="text"
                        value={input}
                        disabled={disabled}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                onSubmitInput();
                            }
                        }}
                        placeholder="Manage users..."
                        className={[
                            'min-w-0 flex-1 bg-transparent outline-none',
                            'text-[16px] font-normal leading-[100%] tracking-[-0.02em]',
                            isDark
                                ? 'text-white placeholder:text-[#717680]'
                                : 'text-[#252B37] placeholder:text-[#717680]',
                            disabled ? 'cursor-not-allowed' : '',
                        ].join(' ')}
                    />

                    <input
                        type="file"
                        multiple
                        accept="*/*"
                        className="hidden"
                        ref={fileInputRef}
                        onChange={() => undefined}
                    />

                    <button
                        type="button"
                        disabled={disabled}
                        onClick={() => fileInputRef.current?.click()}
                        className={[
                            'mx-1 flex h-7 w-7 shrink-0 items-center justify-center',
                            disabled
                                ? 'cursor-not-allowed opacity-50'
                                : 'cursor-pointer',
                        ].join(' ')}
                        aria-label="Attach file">
                        <img
                            src={
                                isDark
                                    ? '/assets/chat/file-attach-white.svg'
                                    : '/assets/chat/file-attach.svg'
                            }
                            className="h-7 w-7 object-contain"
                            alt=""
                        />
                    </button>

                    <button
                        type="button"
                        disabled={disabled}
                        onClick={onSubmitInput}
                        className={[
                            'ml-1 flex h-7 w-7 shrink-0 items-center justify-center',
                            disabled
                                ? 'cursor-not-allowed opacity-50'
                                : 'cursor-pointer',
                        ].join(' ')}
                        aria-label="Send">
                        <img
                            src={
                                isDark
                                    ? '/assets/chat/send-white.svg'
                                    : '/assets/chat/send.svg'
                            }
                            className="h-7 w-7 object-contain"
                            alt=""
                        />
                    </button>
                </div>

                {/* Utility row: Actions only (no Active Trips — Rebecca-only) */}
                <div className="mt-4 flex w-full min-w-0 flex-wrap items-center justify-start gap-x-4 gap-y-2">
                    <button
                        type="button"
                        onClick={toggleActions}
                        disabled={disabled}
                        className={[
                            'mr-6 flex flex-row items-center',
                            disabled
                                ? 'cursor-not-allowed opacity-50'
                                : 'cursor-pointer hover:opacity-90',
                        ].join(' ')}>
                        <p
                            className={`mr-2 dark:text-primary-darkMode transition-colors duration-200 ${
                                showActions
                                    ? 'text-[#252B37] font-medium'
                                    : 'text-[#535862]'
                            }`}>
                            Actions
                        </p>
                        <img
                            src={
                                showActions
                                    ? '/svg/arrows_input.svg'
                                    : isDark
                                      ? '/assets/insights/see-more-icon-white.svg'
                                      : '/assets/layout/dots-gray.svg'
                            }
                            className="h-6 w-6 object-contain opacity-80"
                            alt=""
                        />
                    </button>
                </div>

                {showActions && (
                    <div className="mt-3 flex w-full min-w-0 flex-wrap gap-3">
                        {EXEC_CHAT_ACTIONS.map((action) => (
                            <button
                                key={action.enum}
                                type="button"
                                disabled={disabled}
                                onClick={() => onClickAction(action)}
                                className={[
                                    'rounded-full px-5 py-2.5 text-sm font-medium transition',
                                    'bg-[#F2F4F7] text-[#1D2939] hover:bg-[#E4E7EC] active:scale-[0.98] active:bg-[#D0D5DD]',
                                    'dark:bg-[#1f1f1f] dark:text-[#F2F4F7] dark:hover:bg-[#2a2a2a] dark:active:bg-[#333]',
                                    disabled ? 'cursor-not-allowed opacity-50' : '',
                                ].join(' ')}>
                                {action.name}
                            </button>
                        ))}
                    </div>
                )}
            </section>
            <div
                className="mt-4 mb-4 h-px w-full shrink-0 bg-gray-200 dark:bg-borderColor-darkMode"
                aria-hidden
            />
        </div>
    );
}
